import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Heart, Plus, Calendar, Pill, FileText, Activity, Clock, AlertTriangle,
  User, Phone, Stethoscope, Home, Droplets, ThermometerSun, Scale,
  Moon, Utensils, Footprints, MessageSquare, Bell, Shield, Edit, Trash2,
  ChevronRight, UserCog, AlertCircle, CheckCircle2, XCircle, Smile, Frown, Meh
} from 'lucide-react';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { API } from '../App';

const ElderlyCare = ({ user, setUser }) => {
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Dialogs
  const [showAddProfile, setShowAddProfile] = useState(false);
  const [showCheckIn, setShowCheckIn] = useState(false);
  const [showAddMedication, setShowAddMedication] = useState(false);
  const [showReportFall, setShowReportFall] = useState(false);
  const [showAddNote, setShowAddNote] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  
  // Forms
  const [profileForm, setProfileForm] = useState({
    name: '', date_of_birth: '', gender: 'male', blood_group: '', phone: '', address: '',
    chronic_conditions: '', allergies: '', dietary_restrictions: '',
    mobility_status: 'independent', cognitive_status: '', hearing_status: '', vision_status: '',
    primary_caregiver: '', caregiver_phone: '', emergency_contact_name: '', emergency_contact_phone: '',
    emergency_contact_relation: '', primary_doctor: '', doctor_phone: '', hospital_preference: '', notes: ''
  });
  
  const [checkInForm, setCheckInForm] = useState({
    check_in_date: new Date().toISOString().split('T')[0],
    mood: 'neutral', pain_level: 'none', pain_location: '',
    sleep_quality: 3, sleep_hours: '', appetite: 3,
    blood_pressure_systolic: '', blood_pressure_diastolic: '', heart_rate: '',
    blood_sugar: '', temperature: '', weight: '',
    medications_taken: true, meals_eaten: 3, water_intake: '',
    exercise_done: false, exercise_minutes: '', social_interaction: false,
    fell_today: false, fall_details: '', symptoms: '', notes: '', recorded_by: ''
  });
  
  const [medicationForm, setMedicationForm] = useState({
    medication_name: '', dosage: '', frequency: 'daily', times: '',
    with_food: false, purpose: '', side_effects: '', prescribed_by: '',
    pharmacy: '', refill_date: '', start_date: '', special_instructions: '', is_critical: false
  });
  
  const [fallForm, setFallForm] = useState({
    fall_date: '', location: '', circumstances: '', injuries: '',
    injury_severity: 'none', medical_attention: false, hospitalized: false, notes: ''
  });
  
  const [noteForm, setNoteForm] = useState({
    note_type: 'observation', title: '', content: '', priority: 'low', author: ''
  });

  useEffect(() => { fetchProfiles(); }, []);
  useEffect(() => { if (selectedProfile) fetchDashboard(selectedProfile.id); }, [selectedProfile]);

  const fetchProfiles = async () => {
    try {
      const response = await axios.get(`${API}/elderly/profiles`);
      setProfiles(response.data);
      if (response.data.length > 0 && !selectedProfile) setSelectedProfile(response.data[0]);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load elderly profiles');
      setLoading(false);
    }
  };

  const fetchDashboard = async (profileId) => {
    try {
      const response = await axios.get(`${API}/elderly/dashboard/${profileId}`);
      setDashboard(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    }
  };

  const handleAddProfile = async () => {
    try {
      const data = {
        ...profileForm,
        chronic_conditions: profileForm.chronic_conditions ? profileForm.chronic_conditions.split(',').map(c => c.trim()) : [],
        allergies: profileForm.allergies ? profileForm.allergies.split(',').map(a => a.trim()) : [],
        dietary_restrictions: profileForm.dietary_restrictions ? profileForm.dietary_restrictions.split(',').map(d => d.trim()) : []
      };
      
      if (editingProfile) {
        await axios.put(`${API}/elderly/profiles/${editingProfile.id}`, data);
        toast.success('Profile updated');
      } else {
        await axios.post(`${API}/elderly/profiles`, data);
        toast.success('Elderly profile created');
      }
      
      setShowAddProfile(false);
      setEditingProfile(null);
      resetProfileForm();
      fetchProfiles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving profile');
    }
  };

  const handleCheckIn = async () => {
    try {
      const data = {
        ...checkInForm,
        elderly_id: selectedProfile.id,
        symptoms: checkInForm.symptoms ? checkInForm.symptoms.split(',').map(s => s.trim()) : []
      };
      
      await axios.post(`${API}/elderly/check-ins`, data);
      toast.success('Daily check-in recorded');
      setShowCheckIn(false);
      resetCheckInForm();
      fetchDashboard(selectedProfile.id);
    } catch (error) {
      toast.error('Error recording check-in');
    }
  };

  const handleAddMedication = async () => {
    try {
      const data = {
        ...medicationForm,
        elderly_id: selectedProfile.id,
        times: medicationForm.times.split(',').map(t => t.trim())
      };
      
      await axios.post(`${API}/elderly/medications`, data);
      toast.success('Medication added');
      setShowAddMedication(false);
      resetMedicationForm();
      fetchDashboard(selectedProfile.id);
    } catch (error) {
      toast.error('Error adding medication');
    }
  };

  const handleReportFall = async () => {
    try {
      const data = {
        ...fallForm,
        elderly_id: selectedProfile.id,
        fall_date: new Date(fallForm.fall_date).toISOString(),
        injuries: fallForm.injuries ? fallForm.injuries.split(',').map(i => i.trim()) : []
      };
      
      await axios.post(`${API}/elderly/falls`, data);
      toast.success('Fall incident reported');
      setShowReportFall(false);
      resetFallForm();
      fetchDashboard(selectedProfile.id);
    } catch (error) {
      toast.error('Error reporting fall');
    }
  };

  const handleAddNote = async () => {
    try {
      const data = { ...noteForm, elderly_id: selectedProfile.id };
      await axios.post(`${API}/elderly/notes`, data);
      toast.success('Note added');
      setShowAddNote(false);
      resetNoteForm();
      fetchDashboard(selectedProfile.id);
    } catch (error) {
      toast.error('Error adding note');
    }
  };

  const handleResolveAlert = async (alertId) => {
    try {
      await axios.put(`${API}/elderly/alerts/${alertId}/resolve`, {});
      toast.success('Alert resolved');
      fetchDashboard(selectedProfile.id);
    } catch (error) {
      toast.error('Error resolving alert');
    }
  };

  const resetProfileForm = () => setProfileForm({ name: '', date_of_birth: '', gender: 'male', blood_group: '', phone: '', address: '', chronic_conditions: '', allergies: '', dietary_restrictions: '', mobility_status: 'independent', cognitive_status: '', hearing_status: '', vision_status: '', primary_caregiver: '', caregiver_phone: '', emergency_contact_name: '', emergency_contact_phone: '', emergency_contact_relation: '', primary_doctor: '', doctor_phone: '', hospital_preference: '', notes: '' });
  const resetCheckInForm = () => setCheckInForm({ check_in_date: new Date().toISOString().split('T')[0], mood: 'neutral', pain_level: 'none', pain_location: '', sleep_quality: 3, sleep_hours: '', appetite: 3, blood_pressure_systolic: '', blood_pressure_diastolic: '', heart_rate: '', blood_sugar: '', temperature: '', weight: '', medications_taken: true, meals_eaten: 3, water_intake: '', exercise_done: false, exercise_minutes: '', social_interaction: false, fell_today: false, fall_details: '', symptoms: '', notes: '', recorded_by: '' });
  const resetMedicationForm = () => setMedicationForm({ medication_name: '', dosage: '', frequency: 'daily', times: '', with_food: false, purpose: '', side_effects: '', prescribed_by: '', pharmacy: '', refill_date: '', start_date: '', special_instructions: '', is_critical: false });
  const resetFallForm = () => setFallForm({ fall_date: '', location: '', circumstances: '', injuries: '', injury_severity: 'none', medical_attention: false, hospitalized: false, notes: '' });
  const resetNoteForm = () => setNoteForm({ note_type: 'observation', title: '', content: '', priority: 'low', author: '' });

  const getMoodIcon = (mood) => {
    switch (mood) {
      case 'very_happy': case 'happy': return <Smile className="w-5 h-5 text-green-500" />;
      case 'neutral': return <Meh className="w-5 h-5 text-yellow-500" />;
      case 'sad': case 'very_sad': return <Frown className="w-5 h-5 text-red-500" />;
      default: return <Meh className="w-5 h-5 text-gray-500" />;
    }
  };

  const getPainColor = (level) => {
    switch (level) {
      case 'none': return 'bg-green-100 text-green-700';
      case 'mild': return 'bg-yellow-100 text-yellow-700';
      case 'moderate': return 'bg-orange-100 text-orange-700';
      case 'severe': case 'extreme': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation user={user} setUser={setUser} />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50">
      <Navigation user={user} setUser={setUser} />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent flex items-center gap-3">
              <UserCog className="w-8 h-8 text-purple-600" />
              Elderly Care
            </h1>
            <p className="text-gray-600 mt-1">Comprehensive health management for elderly family members</p>
          </div>
          
          <Button onClick={() => { setEditingProfile(null); resetProfileForm(); setShowAddProfile(true); }} className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 gap-2">
            <Plus className="w-4 h-4" /> Add Elderly Profile
          </Button>
        </div>

        {profiles.length === 0 ? (
          <Card className="text-center py-16">
            <CardContent>
              <UserCog className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No Elderly Profiles</h3>
              <p className="text-gray-500 mb-6">Add elderly family members to start tracking their health</p>
              <Button onClick={() => setShowAddProfile(true)} className="bg-purple-600 hover:bg-purple-700">
                <Plus className="w-4 h-4 mr-2" /> Add First Profile
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid lg:grid-cols-4 gap-6">
            {/* Profiles Sidebar */}
            <div className="lg:col-span-1">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Heart className="w-5 h-5 text-purple-600" />
                    Elderly Profiles ({profiles.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {profiles.map((profile) => (
                    <div key={profile.id} className={`p-3 rounded-lg cursor-pointer transition-all ${selectedProfile?.id === profile.id ? 'bg-purple-50 border-2 border-purple-200' : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'}`} onClick={() => setSelectedProfile(profile)}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center text-white">
                            <UserCog className="w-5 h-5" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-800">{profile.name}</p>
                            <p className="text-xs text-gray-500">{profile.age} years old</p>
                          </div>
                        </div>
                        <ChevronRight className={`w-4 h-4 text-gray-400 ${selectedProfile?.id === profile.id ? 'rotate-90' : ''}`} />
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3 space-y-6">
              {selectedProfile && dashboard && (
                <>
                  {/* Profile Header */}
                  <Card className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
                            <UserCog className="w-8 h-8" />
                          </div>
                          <div>
                            <h2 className="text-2xl font-bold">{dashboard.profile.name}</h2>
                            <p className="text-white/80">{dashboard.profile.age} years old • {dashboard.profile.gender}</p>
                            <div className="flex gap-2 mt-1">
                              {dashboard.profile.blood_group && <Badge className="bg-white/20 text-white">🩸 {dashboard.profile.blood_group}</Badge>}
                              <Badge className="bg-white/20 text-white">🚶 {dashboard.profile.mobility_status}</Badge>
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col gap-2">
                          <Button size="sm" className="bg-white/20 hover:bg-white/30 text-white" onClick={() => setShowCheckIn(true)}>
                            <Activity className="w-4 h-4 mr-1" /> Daily Check-In
                          </Button>
                          <Button size="sm" variant="outline" className="border-white/30 text-white hover:bg-white/20" onClick={() => setShowReportFall(true)}>
                            <AlertTriangle className="w-4 h-4 mr-1" /> Report Fall
                          </Button>
                        </div>
                      </div>
                      
                      {/* Quick Info */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                        {dashboard.profile.chronic_conditions?.length > 0 && (
                          <div className="bg-white/10 rounded-lg p-2 text-center">
                            <p className="text-xs text-white/70">Conditions</p>
                            <p className="font-semibold">{dashboard.profile.chronic_conditions.length}</p>
                          </div>
                        )}
                        <div className="bg-white/10 rounded-lg p-2 text-center">
                          <p className="text-xs text-white/70">Medications</p>
                          <p className="font-semibold">{dashboard.medications?.count || 0}</p>
                        </div>
                        <div className="bg-white/10 rounded-lg p-2 text-center">
                          <p className="text-xs text-white/70">Falls (30d)</p>
                          <p className="font-semibold">{dashboard.falls?.last_30_days || 0}</p>
                        </div>
                        <div className="bg-white/10 rounded-lg p-2 text-center">
                          <p className="text-xs text-white/70">Alerts</p>
                          <p className="font-semibold">{dashboard.alerts?.unresolved_count || 0}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Alerts Banner */}
                  {dashboard.alerts?.unresolved_count > 0 && (
                    <Card className="bg-red-50 border-red-200">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <AlertCircle className="w-6 h-6 text-red-600" />
                          <div className="flex-1">
                            <p className="font-semibold text-red-800">{dashboard.alerts.unresolved_count} Unresolved Alert(s)</p>
                            {dashboard.alerts.recent?.[0] && (
                              <p className="text-sm text-red-600">{dashboard.alerts.recent[0].description}</p>
                            )}
                          </div>
                          {dashboard.alerts.recent?.[0] && (
                            <Button size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-100" onClick={() => handleResolveAlert(dashboard.alerts.recent[0].id)}>
                              <CheckCircle2 className="w-4 h-4 mr-1" /> Resolve
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Health Analysis */}
                  {dashboard.health_analysis?.alerts?.length > 0 && (
                    <Card className="bg-yellow-50 border-yellow-200">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg text-yellow-800 flex items-center gap-2">
                          <Bell className="w-5 h-5" /> Health Insights
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {dashboard.health_analysis.alerts.map((alert, idx) => (
                            <div key={`item-${idx}`} className="flex items-center gap-2 text-sm">
                              <Badge className={alert.priority === 'high' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}>
                                {alert.priority}
                              </Badge>
                              <span className="text-gray-700">{alert.message}</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Tabs */}
                  <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="grid grid-cols-5 gap-1">
                      <TabsTrigger value="overview" className="text-xs sm:text-sm"><Activity className="w-4 h-4 mr-1 hidden sm:inline" /> Overview</TabsTrigger>
                      <TabsTrigger value="vitals" className="text-xs sm:text-sm"><Heart className="w-4 h-4 mr-1 hidden sm:inline" /> Vitals</TabsTrigger>
                      <TabsTrigger value="medications" className="text-xs sm:text-sm"><Pill className="w-4 h-4 mr-1 hidden sm:inline" /> Meds</TabsTrigger>
                      <TabsTrigger value="falls" className="text-xs sm:text-sm"><AlertTriangle className="w-4 h-4 mr-1 hidden sm:inline" /> Falls</TabsTrigger>
                      <TabsTrigger value="notes" className="text-xs sm:text-sm"><MessageSquare className="w-4 h-4 mr-1 hidden sm:inline" /> Notes</TabsTrigger>
                    </TabsList>

                    <TabsContent value="overview" className="mt-4 space-y-4">
                      {/* Latest Check-in */}
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-lg flex items-center justify-between">
                            <span className="flex items-center gap-2"><Activity className="w-5 h-5 text-purple-600" /> Latest Check-In</span>
                            <Button size="sm" onClick={() => setShowCheckIn(true)}>New Check-In</Button>
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          {dashboard.latest_check_in ? (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              <div className="bg-gray-50 rounded-lg p-3 text-center">
                                <p className="text-xs text-gray-500 mb-1">Mood</p>
                                {getMoodIcon(dashboard.latest_check_in.mood)}
                                <p className="text-sm font-medium capitalize mt-1">{dashboard.latest_check_in.mood?.replace('_', ' ')}</p>
                              </div>
                              <div className="bg-gray-50 rounded-lg p-3 text-center">
                                <p className="text-xs text-gray-500 mb-1">Pain Level</p>
                                <Badge className={getPainColor(dashboard.latest_check_in.pain_level)}>
                                  {dashboard.latest_check_in.pain_level}
                                </Badge>
                              </div>
                              <div className="bg-gray-50 rounded-lg p-3 text-center">
                                <p className="text-xs text-gray-500 mb-1">Sleep</p>
                                <p className="text-lg font-bold text-purple-600">{dashboard.latest_check_in.sleep_hours || '-'}h</p>
                                <p className="text-xs text-gray-500">Quality: {dashboard.latest_check_in.sleep_quality}/5</p>
                              </div>
                              <div className="bg-gray-50 rounded-lg p-3 text-center">
                                <p className="text-xs text-gray-500 mb-1">Medications</p>
                                {dashboard.latest_check_in.medications_taken ? (
                                  <CheckCircle2 className="w-6 h-6 text-green-500 mx-auto" />
                                ) : (
                                  <XCircle className="w-6 h-6 text-red-500 mx-auto" />
                                )}
                                <p className="text-xs mt-1">{dashboard.latest_check_in.medications_taken ? 'Taken' : 'Missed'}</p>
                              </div>
                            </div>
                          ) : (
                            <p className="text-gray-500 text-center py-4">No check-ins recorded yet</p>
                          )}
                        </CardContent>
                      </Card>

                      {/* Today's Medications */}
                      <Card>
                        <CardHeader className="pb-3 flex flex-row items-center justify-between">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <Pill className="w-5 h-5 text-purple-600" /> Today's Medications
                          </CardTitle>
                          <Button size="sm" variant="outline" onClick={() => setShowAddMedication(true)}>
                            <Plus className="w-4 h-4 mr-1" /> Add
                          </Button>
                        </CardHeader>
                        <CardContent>
                          {dashboard.medications?.today_schedule?.length > 0 ? (
                            <div className="space-y-2">
                              {dashboard.medications.today_schedule.map((med, idx) => (
                                <div key={`item-${idx}`} className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                                  <div className="flex items-center gap-3">
                                    <Pill className={`w-5 h-5 ${med.is_critical ? 'text-red-500' : 'text-purple-500'}`} />
                                    <div>
                                      <p className="font-medium text-gray-800">{med.name}</p>
                                      <p className="text-sm text-gray-500">{med.dosage} {med.with_food && '• With food'}</p>
                                    </div>
                                  </div>
                                  <div className="flex gap-1">
                                    {med.times.map((t, i) => (
                                      <Badge key={`item-${i}`} variant="outline" className="bg-white">{t}</Badge>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-gray-500 text-center py-4">No medications scheduled</p>
                          )}
                        </CardContent>
                      </Card>

                      {/* Emergency Contacts */}
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <Shield className="w-5 h-5 text-red-500" /> Emergency Information
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="grid md:grid-cols-3 gap-4">
                            {dashboard.profile.emergency_contact_name && (
                              <div className="bg-red-50 rounded-lg p-3">
                                <p className="text-xs text-red-500 font-medium">Emergency Contact</p>
                                <p className="font-semibold text-gray-800">{dashboard.profile.emergency_contact_name}</p>
                                <p className="text-sm text-gray-600">{dashboard.profile.emergency_contact_relation}</p>
                                <p className="text-sm text-red-600 flex items-center gap-1 mt-1">
                                  <Phone className="w-3 h-3" /> {dashboard.profile.emergency_contact_phone}
                                </p>
                              </div>
                            )}
                            {dashboard.profile.primary_doctor && (
                              <div className="bg-blue-50 rounded-lg p-3">
                                <p className="text-xs text-blue-500 font-medium">Primary Doctor</p>
                                <p className="font-semibold text-gray-800">{dashboard.profile.primary_doctor}</p>
                                <p className="text-sm text-blue-600 flex items-center gap-1 mt-1">
                                  <Phone className="w-3 h-3" /> {dashboard.profile.doctor_phone}
                                </p>
                              </div>
                            )}
                            {dashboard.profile.hospital_preference && (
                              <div className="bg-green-50 rounded-lg p-3">
                                <p className="text-xs text-green-500 font-medium">Preferred Hospital</p>
                                <p className="font-semibold text-gray-800">{dashboard.profile.hospital_preference}</p>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </TabsContent>

                    <TabsContent value="vitals" className="mt-4">
                      <Card>
                        <CardHeader>
                          <CardTitle>Vitals History</CardTitle>
                          <CardDescription>Recent vital signs from check-ins</CardDescription>
                        </CardHeader>
                        <CardContent>
                          {dashboard.latest_check_in ? (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              <div className="bg-red-50 rounded-lg p-4 text-center">
                                <Heart className="w-6 h-6 text-red-500 mx-auto mb-2" />
                                <p className="text-xs text-gray-500">Blood Pressure</p>
                                <p className="text-xl font-bold text-red-600">
                                  {dashboard.latest_check_in.blood_pressure_systolic || '-'}/{dashboard.latest_check_in.blood_pressure_diastolic || '-'}
                                </p>
                                <p className="text-xs text-gray-500">mmHg</p>
                              </div>
                              <div className="bg-pink-50 rounded-lg p-4 text-center">
                                <Activity className="w-6 h-6 text-pink-500 mx-auto mb-2" />
                                <p className="text-xs text-gray-500">Heart Rate</p>
                                <p className="text-xl font-bold text-pink-600">{dashboard.latest_check_in.heart_rate || '-'}</p>
                                <p className="text-xs text-gray-500">bpm</p>
                              </div>
                              <div className="bg-orange-50 rounded-lg p-4 text-center">
                                <ThermometerSun className="w-6 h-6 text-orange-500 mx-auto mb-2" />
                                <p className="text-xs text-gray-500">Temperature</p>
                                <p className="text-xl font-bold text-orange-600">{dashboard.latest_check_in.temperature || '-'}</p>
                                <p className="text-xs text-gray-500">°F</p>
                              </div>
                              <div className="bg-blue-50 rounded-lg p-4 text-center">
                                <Droplets className="w-6 h-6 text-blue-500 mx-auto mb-2" />
                                <p className="text-xs text-gray-500">Blood Sugar</p>
                                <p className="text-xl font-bold text-blue-600">{dashboard.latest_check_in.blood_sugar || '-'}</p>
                                <p className="text-xs text-gray-500">mg/dL</p>
                              </div>
                              <div className="bg-green-50 rounded-lg p-4 text-center">
                                <Scale className="w-6 h-6 text-green-500 mx-auto mb-2" />
                                <p className="text-xs text-gray-500">Weight</p>
                                <p className="text-xl font-bold text-green-600">{dashboard.latest_check_in.weight || '-'}</p>
                                <p className="text-xs text-gray-500">kg</p>
                              </div>
                              <div className="bg-indigo-50 rounded-lg p-4 text-center">
                                <Moon className="w-6 h-6 text-indigo-500 mx-auto mb-2" />
                                <p className="text-xs text-gray-500">Sleep</p>
                                <p className="text-xl font-bold text-indigo-600">{dashboard.latest_check_in.sleep_hours || '-'}h</p>
                                <p className="text-xs text-gray-500">Quality: {dashboard.latest_check_in.sleep_quality}/5</p>
                              </div>
                              <div className="bg-amber-50 rounded-lg p-4 text-center">
                                <Utensils className="w-6 h-6 text-amber-500 mx-auto mb-2" />
                                <p className="text-xs text-gray-500">Meals Eaten</p>
                                <p className="text-xl font-bold text-amber-600">{dashboard.latest_check_in.meals_eaten || '-'}</p>
                                <p className="text-xs text-gray-500">Appetite: {dashboard.latest_check_in.appetite}/5</p>
                              </div>
                              <div className="bg-cyan-50 rounded-lg p-4 text-center">
                                <Droplets className="w-6 h-6 text-cyan-500 mx-auto mb-2" />
                                <p className="text-xs text-gray-500">Water Intake</p>
                                <p className="text-xl font-bold text-cyan-600">{dashboard.latest_check_in.water_intake || '-'}</p>
                                <p className="text-xs text-gray-500">glasses</p>
                              </div>
                            </div>
                          ) : (
                            <p className="text-gray-500 text-center py-8">No vitals recorded. Complete a daily check-in to record vitals.</p>
                          )}
                        </CardContent>
                      </Card>
                    </TabsContent>

                    <TabsContent value="medications" className="mt-4">
                      <Card>
                        <CardHeader className="flex flex-row items-center justify-between">
                          <CardTitle>Medications</CardTitle>
                          <Button size="sm" onClick={() => setShowAddMedication(true)}><Plus className="w-4 h-4 mr-1" /> Add Medication</Button>
                        </CardHeader>
                        <CardContent>
                          {dashboard.medications?.today_schedule?.length > 0 ? (
                            <div className="space-y-3">
                              {dashboard.medications.today_schedule.map((med, idx) => (
                                <div key={`item-${idx}`} className="p-4 border rounded-lg">
                                  <div className="flex items-start justify-between">
                                    <div className="flex items-start gap-3">
                                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${med.is_critical ? 'bg-red-100' : 'bg-purple-100'}`}>
                                        <Pill className={`w-5 h-5 ${med.is_critical ? 'text-red-600' : 'text-purple-600'}`} />
                                      </div>
                                      <div>
                                        <p className="font-semibold text-gray-800">{med.name}</p>
                                        <p className="text-sm text-gray-600">{med.dosage}</p>
                                        {med.with_food && <p className="text-xs text-orange-600">Take with food</p>}
                                      </div>
                                    </div>
                                    <div className="flex flex-wrap gap-1 justify-end">
                                      {med.times.map((t, i) => (
                                        <Badge key={`item-${i}`} className="bg-purple-100 text-purple-700">{t}</Badge>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-gray-500 text-center py-8">No medications added</p>
                          )}
                        </CardContent>
                      </Card>
                    </TabsContent>

                    <TabsContent value="falls" className="mt-4">
                      <Card>
                        <CardHeader className="flex flex-row items-center justify-between">
                          <div>
                            <CardTitle>Fall History</CardTitle>
                            <CardDescription>{dashboard.falls?.last_30_days || 0} falls in the last 30 days</CardDescription>
                          </div>
                          <Button size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => setShowReportFall(true)}>
                            <AlertTriangle className="w-4 h-4 mr-1" /> Report Fall
                          </Button>
                        </CardHeader>
                        <CardContent>
                          {dashboard.falls?.recent?.length > 0 ? (
                            <div className="space-y-3">
                              {dashboard.falls.recent.map((fall, idx) => (
                                <div key={`item-${idx}`} className="p-4 border border-red-100 bg-red-50 rounded-lg">
                                  <div className="flex items-start justify-between">
                                    <div>
                                      <p className="font-semibold text-red-800">Fall at {fall.location}</p>
                                      <p className="text-sm text-red-600">{new Date(fall.fall_date).toLocaleString()}</p>
                                      {fall.circumstances && <p className="text-sm text-gray-600 mt-1">{fall.circumstances}</p>}
                                      {fall.injuries?.length > 0 && (
                                        <div className="flex gap-1 mt-2">
                                          {fall.injuries.map((inj, i) => (
                                            <Badge key={`item-${i}`} variant="outline" className="border-red-300 text-red-600">{inj}</Badge>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                    <div className="text-right">
                                      {fall.medical_attention && <Badge className="bg-orange-100 text-orange-700">Medical Attention</Badge>}
                                      {fall.hospitalized && <Badge className="bg-red-100 text-red-700 ml-1">Hospitalized</Badge>}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-8">
                              <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-2" />
                              <p className="text-green-600 font-medium">No falls recorded</p>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </TabsContent>

                    <TabsContent value="notes" className="mt-4">
                      <Card>
                        <CardHeader className="flex flex-row items-center justify-between">
                          <CardTitle>Caregiver Notes</CardTitle>
                          <Button size="sm" onClick={() => setShowAddNote(true)}><Plus className="w-4 h-4 mr-1" /> Add Note</Button>
                        </CardHeader>
                        <CardContent>
                          {dashboard.notes?.recent?.length > 0 ? (
                            <div className="space-y-3">
                              {dashboard.notes.recent.map((note, idx) => (
                                <div key={`item-${idx}`} className="p-4 border rounded-lg">
                                  <div className="flex items-start justify-between">
                                    <div>
                                      <div className="flex items-center gap-2 mb-1">
                                        <Badge variant="outline">{note.note_type}</Badge>
                                        <Badge className={note.priority === 'high' || note.priority === 'critical' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}>{note.priority}</Badge>
                                      </div>
                                      <p className="font-semibold text-gray-800">{note.title}</p>
                                      <p className="text-sm text-gray-600 mt-1">{note.content}</p>
                                      {note.author && <p className="text-xs text-gray-400 mt-2">By: {note.author}</p>}
                                    </div>
                                    <p className="text-xs text-gray-400">{new Date(note.created_at).toLocaleDateString()}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-gray-500 text-center py-8">No notes added</p>
                          )}
                        </CardContent>
                      </Card>
                    </TabsContent>
                  </Tabs>
                </>
              )}
            </div>
          </div>
        )}

        {/* Add Profile Dialog */}
        <Dialog open={showAddProfile} onOpenChange={setShowAddProfile}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingProfile ? 'Edit' : 'Add'} Elderly Profile</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Name *</Label><Input value={profileForm.name} onChange={(e) => setProfileForm({...profileForm, name: e.target.value})} /></div>
                <div><Label>Date of Birth *</Label><Input type="date" value={profileForm.date_of_birth} onChange={(e) => setProfileForm({...profileForm, date_of_birth: e.target.value})} /></div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div><Label>Gender</Label><Select value={profileForm.gender} onValueChange={(v) => setProfileForm({...profileForm, gender: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="male">Male</SelectItem><SelectItem value="female">Female</SelectItem></SelectContent></Select></div>
                <div><Label>Blood Group</Label><Select value={profileForm.blood_group} onValueChange={(v) => setProfileForm({...profileForm, blood_group: v})}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{['A+','A-','B+','B-','AB+','AB-','O+','O-'].map(bg => <SelectItem key={bg} value={bg}>{bg}</SelectItem>)}</SelectContent></Select></div>
                <div><Label>Phone</Label><Input value={profileForm.phone} onChange={(e) => setProfileForm({...profileForm, phone: e.target.value})} /></div>
              </div>
              <div><Label>Address</Label><Input value={profileForm.address} onChange={(e) => setProfileForm({...profileForm, address: e.target.value})} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Chronic Conditions (comma separated)</Label><Input value={profileForm.chronic_conditions} onChange={(e) => setProfileForm({...profileForm, chronic_conditions: e.target.value})} placeholder="Diabetes, Hypertension" /></div>
                <div><Label>Allergies (comma separated)</Label><Input value={profileForm.allergies} onChange={(e) => setProfileForm({...profileForm, allergies: e.target.value})} placeholder="Penicillin, Nuts" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Mobility Status</Label><Select value={profileForm.mobility_status} onValueChange={(v) => setProfileForm({...profileForm, mobility_status: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="independent">Independent</SelectItem><SelectItem value="minimal_assistance">Minimal Assistance</SelectItem><SelectItem value="moderate_assistance">Moderate Assistance</SelectItem><SelectItem value="extensive_assistance">Extensive Assistance</SelectItem><SelectItem value="bedbound">Bedbound</SelectItem></SelectContent></Select></div>
                <div><Label>Cognitive Status</Label><Input value={profileForm.cognitive_status} onChange={(e) => setProfileForm({...profileForm, cognitive_status: e.target.value})} placeholder="Alert, Mild dementia, etc." /></div>
              </div>
              <div className="border-t pt-4"><p className="font-medium text-gray-700 mb-2">Emergency Contacts</p></div>
              <div className="grid grid-cols-3 gap-4">
                <div><Label>Emergency Contact Name</Label><Input value={profileForm.emergency_contact_name} onChange={(e) => setProfileForm({...profileForm, emergency_contact_name: e.target.value})} /></div>
                <div><Label>Phone</Label><Input value={profileForm.emergency_contact_phone} onChange={(e) => setProfileForm({...profileForm, emergency_contact_phone: e.target.value})} /></div>
                <div><Label>Relation</Label><Input value={profileForm.emergency_contact_relation} onChange={(e) => setProfileForm({...profileForm, emergency_contact_relation: e.target.value})} placeholder="Son, Daughter" /></div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div><Label>Primary Doctor</Label><Input value={profileForm.primary_doctor} onChange={(e) => setProfileForm({...profileForm, primary_doctor: e.target.value})} /></div>
                <div><Label>Doctor Phone</Label><Input value={profileForm.doctor_phone} onChange={(e) => setProfileForm({...profileForm, doctor_phone: e.target.value})} /></div>
                <div><Label>Preferred Hospital</Label><Input value={profileForm.hospital_preference} onChange={(e) => setProfileForm({...profileForm, hospital_preference: e.target.value})} /></div>
              </div>
              <div><Label>Notes</Label><Textarea value={profileForm.notes} onChange={(e) => setProfileForm({...profileForm, notes: e.target.value})} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddProfile(false)}>Cancel</Button>
              <Button onClick={handleAddProfile} className="bg-purple-600 hover:bg-purple-700">{editingProfile ? 'Update' : 'Add'} Profile</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Daily Check-In Dialog */}
        <Dialog open={showCheckIn} onOpenChange={setShowCheckIn}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Daily Health Check-In for {selectedProfile?.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div><Label>Date</Label><Input type="date" value={checkInForm.check_in_date} onChange={(e) => setCheckInForm({...checkInForm, check_in_date: e.target.value})} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Mood</Label><Select value={checkInForm.mood} onValueChange={(v) => setCheckInForm({...checkInForm, mood: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="very_happy">😄 Very Happy</SelectItem><SelectItem value="happy">🙂 Happy</SelectItem><SelectItem value="neutral">😐 Neutral</SelectItem><SelectItem value="sad">😔 Sad</SelectItem><SelectItem value="very_sad">😢 Very Sad</SelectItem></SelectContent></Select></div>
                <div><Label>Pain Level</Label><Select value={checkInForm.pain_level} onValueChange={(v) => setCheckInForm({...checkInForm, pain_level: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="none">None</SelectItem><SelectItem value="mild">Mild</SelectItem><SelectItem value="moderate">Moderate</SelectItem><SelectItem value="severe">Severe</SelectItem><SelectItem value="extreme">Extreme</SelectItem></SelectContent></Select></div>
              </div>
              {checkInForm.pain_level !== 'none' && <div><Label>Pain Location</Label><Input value={checkInForm.pain_location} onChange={(e) => setCheckInForm({...checkInForm, pain_location: e.target.value})} placeholder="Back, Knee, Head" /></div>}
              <div className="grid grid-cols-3 gap-4">
                <div><Label>Sleep Quality (1-5)</Label><Input type="number" min="1" max="5" value={checkInForm.sleep_quality} onChange={(e) => setCheckInForm({...checkInForm, sleep_quality: parseInt(e.target.value)})} /></div>
                <div><Label>Sleep Hours</Label><Input type="number" step="0.5" value={checkInForm.sleep_hours} onChange={(e) => setCheckInForm({...checkInForm, sleep_hours: e.target.value})} /></div>
                <div><Label>Appetite (1-5)</Label><Input type="number" min="1" max="5" value={checkInForm.appetite} onChange={(e) => setCheckInForm({...checkInForm, appetite: parseInt(e.target.value)})} /></div>
              </div>
              <div className="border-t pt-4"><p className="font-medium text-gray-700 mb-2">Vitals (Optional)</p></div>
              <div className="grid grid-cols-4 gap-4">
                <div><Label>BP Systolic</Label><Input type="number" value={checkInForm.blood_pressure_systolic} onChange={(e) => setCheckInForm({...checkInForm, blood_pressure_systolic: e.target.value})} placeholder="120" /></div>
                <div><Label>BP Diastolic</Label><Input type="number" value={checkInForm.blood_pressure_diastolic} onChange={(e) => setCheckInForm({...checkInForm, blood_pressure_diastolic: e.target.value})} placeholder="80" /></div>
                <div><Label>Heart Rate</Label><Input type="number" value={checkInForm.heart_rate} onChange={(e) => setCheckInForm({...checkInForm, heart_rate: e.target.value})} placeholder="72" /></div>
                <div><Label>Blood Sugar</Label><Input type="number" value={checkInForm.blood_sugar} onChange={(e) => setCheckInForm({...checkInForm, blood_sugar: e.target.value})} placeholder="100" /></div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div><Label>Temperature (°F)</Label><Input type="number" step="0.1" value={checkInForm.temperature} onChange={(e) => setCheckInForm({...checkInForm, temperature: e.target.value})} placeholder="98.6" /></div>
                <div><Label>Weight (kg)</Label><Input type="number" step="0.1" value={checkInForm.weight} onChange={(e) => setCheckInForm({...checkInForm, weight: e.target.value})} /></div>
                <div><Label>Water (glasses)</Label><Input type="number" value={checkInForm.water_intake} onChange={(e) => setCheckInForm({...checkInForm, water_intake: e.target.value})} /></div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="flex items-center gap-2"><Switch checked={checkInForm.medications_taken} onCheckedChange={(c) => setCheckInForm({...checkInForm, medications_taken: c})} /><Label>Medications Taken</Label></div>
                <div className="flex items-center gap-2"><Switch checked={checkInForm.exercise_done} onCheckedChange={(c) => setCheckInForm({...checkInForm, exercise_done: c})} /><Label>Exercised Today</Label></div>
                <div className="flex items-center gap-2"><Switch checked={checkInForm.social_interaction} onCheckedChange={(c) => setCheckInForm({...checkInForm, social_interaction: c})} /><Label>Social Interaction</Label></div>
              </div>
              <div className="flex items-center gap-2 p-3 bg-red-50 rounded-lg">
                <Switch checked={checkInForm.fell_today} onCheckedChange={(c) => setCheckInForm({...checkInForm, fell_today: c})} />
                <Label className="text-red-700 font-medium">Fell Today?</Label>
              </div>
              {checkInForm.fell_today && <div><Label>Fall Details</Label><Textarea value={checkInForm.fall_details} onChange={(e) => setCheckInForm({...checkInForm, fall_details: e.target.value})} placeholder="Describe what happened" /></div>}
              <div><Label>Any Symptoms (comma separated)</Label><Input value={checkInForm.symptoms} onChange={(e) => setCheckInForm({...checkInForm, symptoms: e.target.value})} placeholder="Dizziness, Fatigue, Cough" /></div>
              <div><Label>Notes</Label><Textarea value={checkInForm.notes} onChange={(e) => setCheckInForm({...checkInForm, notes: e.target.value})} /></div>
              <div><Label>Recorded By</Label><Input value={checkInForm.recorded_by} onChange={(e) => setCheckInForm({...checkInForm, recorded_by: e.target.value})} placeholder="Caregiver name" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCheckIn(false)}>Cancel</Button>
              <Button onClick={handleCheckIn} className="bg-purple-600 hover:bg-purple-700">Save Check-In</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Medication Dialog */}
        <Dialog open={showAddMedication} onOpenChange={setShowAddMedication}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle>Add Medication</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Medication Name *</Label><Input value={medicationForm.medication_name} onChange={(e) => setMedicationForm({...medicationForm, medication_name: e.target.value})} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Dosage *</Label><Input value={medicationForm.dosage} onChange={(e) => setMedicationForm({...medicationForm, dosage: e.target.value})} placeholder="500mg" /></div>
                <div><Label>Frequency</Label><Select value={medicationForm.frequency} onValueChange={(v) => setMedicationForm({...medicationForm, frequency: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="daily">Once Daily</SelectItem><SelectItem value="twice_daily">Twice Daily</SelectItem><SelectItem value="thrice_daily">Thrice Daily</SelectItem><SelectItem value="weekly">Weekly</SelectItem></SelectContent></Select></div>
              </div>
              <div><Label>Times (comma separated) *</Label><Input value={medicationForm.times} onChange={(e) => setMedicationForm({...medicationForm, times: e.target.value})} placeholder="08:00, 20:00" /></div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2"><Switch checked={medicationForm.with_food} onCheckedChange={(c) => setMedicationForm({...medicationForm, with_food: c})} /><Label>Take with food</Label></div>
                <div className="flex items-center gap-2"><Switch checked={medicationForm.is_critical} onCheckedChange={(c) => setMedicationForm({...medicationForm, is_critical: c})} /><Label className="text-red-600">Critical</Label></div>
              </div>
              <div><Label>Purpose</Label><Input value={medicationForm.purpose} onChange={(e) => setMedicationForm({...medicationForm, purpose: e.target.value})} placeholder="For blood pressure" /></div>
              <div><Label>Start Date *</Label><Input type="date" value={medicationForm.start_date} onChange={(e) => setMedicationForm({...medicationForm, start_date: e.target.value})} /></div>
              <div><Label>Special Instructions</Label><Textarea value={medicationForm.special_instructions} onChange={(e) => setMedicationForm({...medicationForm, special_instructions: e.target.value})} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddMedication(false)}>Cancel</Button>
              <Button onClick={handleAddMedication} className="bg-purple-600">Add Medication</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Report Fall Dialog */}
        <Dialog open={showReportFall} onOpenChange={setShowReportFall}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle className="text-red-600">Report Fall Incident</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Date & Time *</Label><Input type="datetime-local" value={fallForm.fall_date} onChange={(e) => setFallForm({...fallForm, fall_date: e.target.value})} /></div>
              <div><Label>Location *</Label><Input value={fallForm.location} onChange={(e) => setFallForm({...fallForm, location: e.target.value})} placeholder="Bathroom, Bedroom, Stairs" /></div>
              <div><Label>How did it happen?</Label><Textarea value={fallForm.circumstances} onChange={(e) => setFallForm({...fallForm, circumstances: e.target.value})} placeholder="Describe what happened" /></div>
              <div><Label>Injuries (comma separated)</Label><Input value={fallForm.injuries} onChange={(e) => setFallForm({...fallForm, injuries: e.target.value})} placeholder="Bruise on arm, Cut on head" /></div>
              <div><Label>Injury Severity</Label><Select value={fallForm.injury_severity} onValueChange={(v) => setFallForm({...fallForm, injury_severity: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="none">No Injuries</SelectItem><SelectItem value="minor">Minor</SelectItem><SelectItem value="moderate">Moderate</SelectItem><SelectItem value="severe">Severe</SelectItem></SelectContent></Select></div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2"><Switch checked={fallForm.medical_attention} onCheckedChange={(c) => setFallForm({...fallForm, medical_attention: c})} /><Label>Medical Attention Needed</Label></div>
                <div className="flex items-center gap-2"><Switch checked={fallForm.hospitalized} onCheckedChange={(c) => setFallForm({...fallForm, hospitalized: c})} /><Label>Hospitalized</Label></div>
              </div>
              <div><Label>Additional Notes</Label><Textarea value={fallForm.notes} onChange={(e) => setFallForm({...fallForm, notes: e.target.value})} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowReportFall(false)}>Cancel</Button>
              <Button onClick={handleReportFall} className="bg-red-600 hover:bg-red-700">Report Fall</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Note Dialog */}
        <Dialog open={showAddNote} onOpenChange={setShowAddNote}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle>Add Caregiver Note</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Note Type</Label><Select value={noteForm.note_type} onValueChange={(v) => setNoteForm({...noteForm, note_type: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="observation">Observation</SelectItem><SelectItem value="concern">Concern</SelectItem><SelectItem value="update">Update</SelectItem><SelectItem value="instruction">Instruction</SelectItem></SelectContent></Select></div>
              <div><Label>Title *</Label><Input value={noteForm.title} onChange={(e) => setNoteForm({...noteForm, title: e.target.value})} /></div>
              <div><Label>Content *</Label><Textarea value={noteForm.content} onChange={(e) => setNoteForm({...noteForm, content: e.target.value})} rows={4} /></div>
              <div><Label>Priority</Label><Select value={noteForm.priority} onValueChange={(v) => setNoteForm({...noteForm, priority: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="critical">Critical</SelectItem></SelectContent></Select></div>
              <div><Label>Author</Label><Input value={noteForm.author} onChange={(e) => setNoteForm({...noteForm, author: e.target.value})} placeholder="Your name" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddNote(false)}>Cancel</Button>
              <Button onClick={handleAddNote} className="bg-purple-600">Add Note</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default ElderlyCare;
